'use client'

import { z } from "zod";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/system/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { ReservationFormProps } from "@/types/popadvise";
import { CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useCurrentBranch } from "@/context/current-branch-provider";

const ReservationOccasionType = {
    DATE_NIGHT: 0,
    ANNIVERSARY: 1,
    CELEBRATION: 2,
    BIRTHDAY: 3,
    FAMILY_GATHERING: 4,
    BUSINESS_DINNER: 5,
    FRIENDS_REUNION: 6,
    OTHER: 7,
}

const ReservationOccasionLabels = {
    DATE_NIGHT: "Cita",
    ANNIVERSARY: "Aniversario",
    CELEBRATION: "Celebración",
    BIRTHDAY: "Cumpleaños",
    FAMILY_GATHERING: "Reunión familiar",
    BUSINESS_DINNER: "Cena de negocios",
    FRIENDS_REUNION: "Reunión de amigos",
    OTHER: "Otro"
}

export function ReservationOcassion({ onContinue, onBackStep, userData, setUserData }: {
    onContinue: () => void
    onBackStep: () => void
    userData: ReservationFormProps
    setUserData: (userData: ReservationFormProps) => void
}) {

    const { currentBranch } = useCurrentBranch()

    const formSchema = z.object({
        occasion: z.number({ required_error: "Selecciona un motivo de la reservación" }),
        specialRequest: z.string(),
    })

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            occasion: undefined,
            specialRequest: "",
        }
    })

    const handleOnSubmit = (data: z.infer<typeof formSchema>) => {
        setUserData({
            ...userData,
            occasion: data.occasion,
            specialRequest: data.specialRequest
        })
        onContinue()
    }

    return (
        <div className="p-6 pt-8">
            <CardHeader className="px-0">
                <CardTitle className="text-left text-xl">{currentBranch.name}</CardTitle>
                <CardDescription className="text-[#6C6C6C] text-left">
                    Completa la información
                </CardDescription>
            </CardHeader>
            <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(handleOnSubmit)}>

                    <FormField
                        control={form.control}
                        name="occasion"
                        render={({ field }) => (
                            <FormItem className="mb-4">
                                <Select onValueChange={(value) => field.onChange(Number(value))} defaultValue={field.value?.toString()}>
                                    <FormControl className="dark:text-white">
                                        <SelectTrigger className={`w-full py-6 rounded-lg ${field.value === undefined ? 'text-[#A9ABB2]' : 'text-black'}`}>
                                            <SelectValue placeholder="Seleccionar motivo">
                                                <div className="flex items-center">
                                                    {field.value !== undefined ? ReservationOccasionLabels[Object.keys(ReservationOccasionType)[field.value]] : "Seleccionar motivo"}
                                                </div>
                                            </SelectValue>
                                        </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                        {Object.entries(ReservationOccasionType).map(([key, value]) => (
                                            <SelectItem key={key} value={value.toString()}>
                                                {ReservationOccasionLabels[key]}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="specialRequest"
                        render={({ field }) => (
                            <FormItem>
                                <Textarea
                                    {...field}
                                    className="resize-none w-full h-40 rounded-lg placeholder:text-[#A9ABB2]"
                                    placeholder="¿Tienes alguna solicitud especial?"
                                />
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <footer className="grid grid-cols-1 gap-3 mt-5">
                        <Button
                            type="submit"
                            className={`
                            py-6 rounded-lg w-full font-semibold
                            ${false && 'bg-[#F2F4F7] text-[#A0A9B4]'}    
                        `}
                            disabled={false}
                        >Continuar</Button>
                        <Button
                            type="button"
                            className="py-6 rounded-lg w-full font-semibold m-0 text-[#6C6C6C]"
                            onClick={() => onBackStep()}
                            variant="ghost"
                        >Atras</Button>
                    </footer>
                </form>
            </FormProvider>
        </div>
    )
}